import { ref, computed } from 'vue';
import apiClient from '@/services/api/client';
import { Endpoints } from '@/config/Urls';
import type { ChangelogEntry } from '@/modules/admin/interfaces/ChangelogEntry';

const LAST_CHANGELOG_SEEN_KEY = 'lastChangelogSeenAt';

/**
 * Parse changelog markdown lines into entries.
 * Format: [YYYY-MM-DD] - Description
 */
function parseChangelog(content: string): ChangelogEntry[] {
  if (!content?.trim()) return [];
  const entries: ChangelogEntry[] = [];
  const lines = content.trim().split(/\r?\n/);
  const lineRegex = /^\[(\d{4}-\d{2}-\d{2})\]\s*-\s*(.+)$/;
  for (const line of lines) {
    const m = line.trim().match(lineRegex);
    if (m) {
      entries.push({ date: m[1], text: m[2].trim() });
    }
  }
  return entries;
}

function getLastChangelogSeenAt(): string | null {
  return localStorage.getItem(LAST_CHANGELOG_SEEN_KEY);
}

function setLastChangelogSeenAt(isoDate: string): void {
  localStorage.setItem(LAST_CHANGELOG_SEEN_KEY, isoDate);
}

export function useChangelog() {
  const rawContent = ref<string>('');
  const loading = ref(false);
  const error = ref<Error | null>(null);

  const entries = computed<ChangelogEntry[]>(() => parseChangelog(rawContent.value));

  const lastSeenAt = computed(() => getLastChangelogSeenAt());

  /** Entries that are newer than the user's last seen date (for "What's New" popup). */
  const newEntriesSinceLastSeen = computed<ChangelogEntry[]>(() => {
    const seen = lastSeenAt.value;
    if (!seen) return entries.value; // never seen → show all as new
    return entries.value.filter((e) => e.date > seen);
  });

  const hasNewEntries = computed(() => newEntriesSinceLastSeen.value.length > 0);

  async function fetchChangelog(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await apiClient.get<{ content: string }>(Endpoints.CHANGELOG);
      rawContent.value = data?.content ?? '';
    } catch (e) {
      error.value = e instanceof Error ? e : new Error('Failed to load changelog');
      rawContent.value = '';
    } finally {
      loading.value = false;
    }
  }

  /** Mark changelog as seen (e.g. when user dismisses popup or opens What's New page). */
  function markAsSeen(): void {
    const newest = entries.value[0]?.date;
    if (newest) {
      setLastChangelogSeenAt(newest);
    } else {
      // No entries: use today so we don't show empty "new" again
      setLastChangelogSeenAt(new Date().toISOString().slice(0, 10));
    }
  }

  return {
    rawContent,
    entries,
    newEntriesSinceLastSeen,
    hasNewEntries,
    lastSeenAt,
    loading,
    error,
    fetchChangelog,
    markAsSeen,
    getLastChangelogSeenAt,
    setLastChangelogSeenAt,
  };
}
